export type UsageWindowPreset = "thisMonth" | "last7" | "last30" | "allTime";

export interface UsageWindow {
  from?: string;
  to?: string;
}

const pad = (n: number): string => String(n).padStart(2, "0");

export const toIsoDateOnly = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export type UsagePreset =
  | "thisMonth"
  | "lastDay"
  | "last3Days"
  | "last7Days"
  | "lastMonth"
  | "allTime"
  | "custom";

const daysAgoRange = (days: number): UsageWindow => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - (days - 1));
  return {
    from: `${toIsoDateOnly(start)}T00:00:00`,
    to: `${toIsoDateOnly(now)}T23:59:59`,
  };
};

// Resolve a preset to an actual range. "allTime" and "custom" return an empty
// range here; for "custom" the caller supplies the picked dates instead.
export const buildPeriod = (preset: UsagePreset): UsageWindow => {
  const now = new Date();
  switch (preset) {
    case "thisMonth": {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        from: `${toIsoDateOnly(first)}T00:00:00`,
        to: `${toIsoDateOnly(now)}T23:59:59`,
      };
    }
    case "lastDay":
      return daysAgoRange(1);
    case "last3Days":
      return daysAgoRange(3);
    case "last7Days":
      return daysAgoRange(7);
    case "lastMonth":
      return daysAgoRange(30);
    case "allTime":
    case "custom":
    default:
      return {};
  }
};

export const buildUsageWindow = (preset: UsageWindowPreset): UsageWindow => {
  const now = new Date();
  const to = `${toIsoDateOnly(now)}T23:59:59`;

  switch (preset) {
    case "thisMonth": {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: `${toIsoDateOnly(first)}T00:00:00`, to };
    }
    case "last7": {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      return { from: `${toIsoDateOnly(d)}T00:00:00`, to };
    }
    case "last30": {
      const d = new Date(now);
      d.setDate(d.getDate() - 29);
      return { from: `${toIsoDateOnly(d)}T00:00:00`, to };
    }
    case "allTime":
    default:
      return {};
  }
};

// Turn a native <input type="date"> value ("2026-07-01") into an ISO datetime
// that makes the backend range inclusive of the whole selected day.
export const dateInputToFrom = (value: string): string | undefined =>
  value ? `${value}T00:00:00` : undefined;

export const dateInputToTo = (value: string): string | undefined =>
  value ? `${value}T23:59:59` : undefined;

export const formatTokens = (n: number | undefined): string =>
  (n ?? 0).toLocaleString("en-US");

export const formatUsd = (n: number | undefined): string =>
  `$${(n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;
